import { debounce } from 'lodash-es'
import { useEffect } from 'react'

import { getBoundRange, storage, useSearchFilterStore } from '@/entities/map'
import {
  useMapStore,
  useSelectedMarkerStore,
  useSidebarStore,
  useViewingRoomStore,
} from '@/entities/map'
import {
  mapManager,
  type Map,
  useResizingMap,
  eventManager,
} from '@/shared/lib'

type Props = {
  updateOverlays: (options: { resetRoom?: boolean }) => void
  clearOverlays: () => void
}

export const useMapEvent = ({ updateOverlays, clearOverlays }: Props) => {
  const {
    isResizing,
    isProgrammaticZoomChanged,
    previousZoomLevel,
    updateProgrammaticZoomChanged,
    updateResizingStatus,
    keepCurrentMapState,
    handleResizeMap,
  } = useResizingMap()

  const mapActions = useMapStore(state => state.actions)

  const isSameParams = useMapStore(state => state.isSameParams)

  const filterActions = useSearchFilterStore(state => state.actions)

  const resetMarker = useSelectedMarkerStore(state => state.actions.resetMarker)

  const resetViewingRoom = useViewingRoomStore(
    state => state.actions.resetViewingRoom,
  )

  const updateCenter = (map: Map) => {
    const center = map.getCenter()

    storage.setCoordinates({
      lat: center.y,
      lng: center.x,
    })
    mapActions.updateCenter({
      lat: center.y,
      lng: center.x,
    })
  }

  const updateViewport = (map: Map, options?: { initial?: boolean }) => {
    const bounds = map.getBounds()
    const zoomLevel = map.getZoom()

    if (isSameParams({ zoomLevel, ...getBoundRange(bounds) })) return

    if (!options?.initial) {
      // 지도 변경 시 검색어 초기화 (최초 진입 시는 검색어 초기화 X)
      filterActions.resetKeyword()
    }

    storage.setZoomLevel(zoomLevel)
    mapActions.updateParams({
      ...getBoundRange(bounds),
      zoomLevel,
    })
  }

  const handleMapChange = (
    options: { resetRoom?: boolean; delay?: boolean; initial?: boolean } = {},
  ) => {
    mapManager.withMap(map => {
      keepCurrentMapState(map)

      updateViewport(map, options)
      updateCenter(map)
      if (!options.delay) updateOverlays(options)
      else debounce(updateOverlays, 200)(options)
    })
  }

  const handleZoomChange = () => {
    if (isProgrammaticZoomChanged()) {
      updateProgrammaticZoomChanged(false)

      return
    }

    if (isResizing() && !isProgrammaticZoomChanged()) {
      updateResizingStatus(false)
    }

    clearOverlays()
    handleMapChange({ delay: true }) // 줌 이후에 이루어지는 의도치 않은 터치를 방지하기 위해 딜레이 처리
    resetViewingRoom()
    resetMarker()

    updateProgrammaticZoomChanged(false)
  }

  const handleDragStart = () => {
    mapManager.setIsDragging(true)
  }

  const handlePinchStart = () => {
    mapManager.setIsDragging(false)
    mapManager.startPinching()
  }

  const handlePinchEnd = () => {
    // 줌 이후에 이루어지는 의도치 않은 터치를 방지하기 위해 1.5초 뒤에 처리
    setTimeout(() => {
      mapManager.endPinching()
    }, 1500)
  }

  const handleDragEnd = () => {
    mapManager.withMap(map => {
      const currentZoomLevel = map.getZoom()

      if (previousZoomLevel() !== currentZoomLevel) {
        clearOverlays()
      }

      handleMapChange()
      resetViewingRoom()
      resetMarker()
    })

    setTimeout(() => {
      mapManager.setIsDragging(false)
    }, 0)
  }

  const handleClick = () => {
    resetViewingRoom()
    resetMarker()
  }

  const handleUpdate = () => {
    handleMapChange()
    resetViewingRoom()
    resetMarker()
  }

  const handleResize = debounce(handleResizeMap, 200)

  const addMapEventListeners = () => {
    mapManager.withMap(map => {
      keepCurrentMapState(map)

      eventManager.addEventListener('zoom_changed', handleZoomChange)
      eventManager.addEventListener('dragstart', handleDragStart)
      eventManager.addEventListener('dragend', handleDragEnd)
      eventManager.addEventListener('resize', handleResize)
      eventManager.addEventListener('click', handleClick)
      eventManager.addEventListener('update', handleUpdate)
      eventManager.addEventListener('pinchstart', handlePinchStart)
      eventManager.addEventListener('pinchend', handlePinchEnd)
    })
  }

  useEffect(() => {
    const sidebarUnsubscribe = useSidebarStore.subscribe(
      state => state.isOpen,
      isOpen => {
        if (isOpen) {
          updateResizingStatus(false)
        } else {
          resetViewingRoom()
          resetMarker()
        }
      },
    )

    return () => {
      sidebarUnsubscribe()
    }
  }, [])

  return {
    updateResizingStatus,
    handleMapChange,
    addMapEventListeners,
  }
}
